import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Modal,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
} from "@mui/material";
import { FormProvider, UseFormReturn, useForm } from "react-hook-form";
import {
  RHFAutocomplete,
  RHFTextField,
} from "../../../../../../../RHFComponents/RHFComponents";
import { pgmedd, pgmedtage } from "../../../../../../../../networking";
import { MedintDataContext } from "../../../../../../Context/MedintContextWrapper";
import { PGMEDD } from "../../../../../../../../services/medintAgendaFormTypes";
import { AllertMessage } from "../../../../MedintDetails";

type TipologieForm = {
  tipologieForm: {
    tipologia: any;
    maxMese: string;
    maxSett: string;
    maxGiorno: string;
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  action: "insert" | "edit" | "copy";
  initialData?: PGMEDD;
  parentMethods: UseFormReturn<any>;
  createdIdPgmedt?: string;
};

const TipologiesModal = ({
  open,
  onClose,
  action,
  initialData,
  parentMethods,
  createdIdPgmedt,
}: Props) => {
  const [loading, setLoading] = useState(false);
  //Stato per gestire eventuali errori
  const [error, setError] = useState<string | null>(null);

  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  const [pendingSubmit, setPendingSubmit] = useState<TipologieForm | null>(
    null,
  );

  const context = useContext(MedintDataContext);

  const modalMethods = useForm<TipologieForm>({
    defaultValues: {
      tipologieForm: {
        tipologia: null,
        maxMese: "0",
        maxSett: "0",
        maxGiorno: "0",
      },
    },
    mode: "onChange",
  });

  // Precompilo il form del modale
  useEffect(() => {
    if (!open) return;

    setError(null);

    if (action === "insert") {
      modalMethods.reset({
        tipologieForm: {
          tipologia: null,
          maxMese: "0",
          maxSett: "0",
          maxGiorno: "0",
        },
      });
      return;
    }

    modalMethods.reset({
      tipologieForm: {
        tipologia: initialData?.CODICETIPOAGENDA ?? "",
        maxMese: initialData?.MAXMESE ?? "0",
        maxSett: initialData?.MAXSETT ?? "0",
        maxGiorno: initialData?.MAXGIO ?? "0",
      },
    });
  }, [open, action, initialData, modalMethods, context?.typologiesTIPOLOGIE]);

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  const askConfirmSubmit = modalMethods.handleSubmit((values) => {
    setPendingSubmit(values);

    setSeverity("warning");
    setMessage(
      action === "insert"
        ? "Confermi l'inserimento della prestazione?"
        : action === "edit"
          ? "Confermi la modifica della prestazione?"
          : "Confermi la copia della prestazione?",
    );
    setShowAllert(true);
  });

  //Refresh Programma attraverso pgmedtage per aggiornare la tabella
  const refreshProgram = async () => {
    const idPgmedt =
      parentMethods.getValues("medIntNuovoProgramma.chiave") ||
      (createdIdPgmedt as string | undefined);
    if (!idPgmedt)
      throw new Error("Manca la chiave agenda (medIntNuovoProgramma.chiave).");

    const response = await pgmedtage({ crud: "R", codicePgmedt: "", idPgmedt });
    const dataPgmedtage = response?.data?.dati?.[0];
    if (!dataPgmedtage) return;

    parentMethods.setValue(
      "medIntNuovoProgramma.tipologie",
      dataPgmedtage.tipoPrenotazione ?? [],
      {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      },
    );
  };

  // Al submit, chiamo pgmedd con crud "C", "U" o "P"
  // (insert, edit, copy) e aggiorno la tabella con pgmedtage
  const submitTypology = async (values: TipologieForm) => {
    if (!pendingSubmit) return;
    setError(null);
    setLoading(true);

    try {
      //Recupero l'idPgmedt da medIntNuovoProgramma
      const idPgmedt =
        parentMethods.getValues("medIntNuovoProgramma.chiave") ||
        (createdIdPgmedt as string | undefined);
      //Se non ho idPgmedt do un errore e chiudo
      if (!idPgmedt) {
        throw new Error("Impossibile salvare: manca l'ID agenda (chiave).");
      }

      //Recupero i dati dal form
      const formValues = pendingSubmit.tipologieForm;

      console.log({ formValues });

      //costante che assume il valore del crud in base ad action
      const crud = action === "insert" ? "C" : action === "edit" ? "U" : "P";

      //Chiamo pgmedd e tramite il crud selezionato, inserisco o aggiorno la tipologia
      const response = await pgmedd({
        crud,
        prgint: initialData?.IDPGMEDD ?? "",
        dati: {
          pgmedd: {
            PGMEDT_PRGINT: idPgmedt,
            PGMEDD_NMAX_MESE: String(formValues.maxMese ?? "0"),
            PGMEDD_NMAX_SETT: String(formValues.maxSett ?? "0"),
            PGMEDD_NMAX_GIO: String(formValues.maxGiorno ?? "0"),
            TPGPRE_CODICE: formValues.tipologia ?? "",
          },
        },
      });

      if (response?.data?.esito === "OK") {
        setLoading(false);
        setSeverity("success");
        setMessage("Operazione completata con successo.");
      } else {
        setSeverity("error");
        setMessage("Salvataggio non riuscito.");
        return;
      }
      await refreshProgram();
      setTimeout(() => onClose(), 800);
    } catch (e: any) {
      console.error("Errore salvataggio tipologia:", e);
      setSeverity("error");
      setMessage(e?.message ?? "Errore durante il salvataggio.");
    } finally {
      setPendingSubmit(null);
      setLoading(false);
    }
  };

  return (
    <>
      <AllertMessage
        dataToSend={pendingSubmit}
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={severity === "warning" ? submitTypology : null}
      />

      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      <Modal
        open={open}
        onClose={(_, reason) => {
          // blocco chiusura su click fuori
          if (reason === "backdropClick") return;

          // blocco su tasto ESC
          if (reason === "escapeKeyDown") return;

          onClose();
        }}
      >
        <Box
          sx={{
            maxHeight: "90vh",
            width: "50%",
            overflow: "auto",
            p: 2,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <FormProvider {...modalMethods}>
            <Tabs value={0} sx={{ mb: 2 }}>
              <Tab
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: "bold",
                  textTransform: "none",
                }}
                label="Tipologie - Dettaglio"
              />
            </Tabs>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <RHFAutocomplete
                  name="tipologieForm.tipologia"
                  control={modalMethods.control}
                  fullWidth
                  label="Tipologie programma"
                  options={context?.typologiesTIPOLOGIE ?? []}
                  getOptionLabel={(t: any) => `${t.DESCRIZIONE ?? ""}`.trim()}
                  mapIn={(code, opts) =>
                    opts.find(
                      (o: any) =>
                        String(o.CODICE).trim() === String(code).trim(),
                    ) ?? null
                  }
                  mapOut={(opt: any) => String(opt?.CODICE ?? "")}
                />
              </Grid>

              <Grid item xs={12}>
                <RHFTextField
                  control={modalMethods.control}
                  name="tipologieForm.maxMese"
                  label="N° massimo mese"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <RHFTextField
                  control={modalMethods.control}
                  name="tipologieForm.maxSett"
                  label="N° massimo settimana"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <RHFTextField
                  control={modalMethods.control}
                  name="tipologieForm.maxGiorno"
                  label="N° massimo giorno"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container justifyContent="space-between">
                  <Button
                    variant="outlinedCustom"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Indietro
                  </Button>

                  <Button
                    variant="containedCustom"
                    disabled={loading}
                    onClick={askConfirmSubmit}
                  >
                    Conferma
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </FormProvider>
        </Box>
      </Modal>
    </>
  );
};

export default TipologiesModal;
